import React from 'react';
import { Badge, Nav, Tab } from 'react-bootstrap';
import { FiCalendar, FiFileText, FiShield, FiTrash2 } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../hooks/useTranslation';
import {
    canAccessCheckForecastBulkDelete,
    getRoleDisplayLabel,
    resolveAdminRole,
} from '../../utils/checkForecastBulkDelete';
import CheckForecastBulkDeleteTab from './CheckForecastBulkDeleteTab';
import CheckForecastBulkDeleteFileTab from './CheckForecastBulkDeleteFileTab';
import './CheckForecastBulkDeleteTab.css';

const PAGE_PREFIX = 'pages.adminTripsForecastBackupCheck.bulkDelete';
const bulkDeleteKey = (path) => PAGE_PREFIX + '.' + path;

export const DELETE_MODE_KEYS = {
    BY_DATE: 'deleteByDate',
    BY_DATE_POLICY: 'deleteByDatePolicy',
    BY_FILE: 'deleteByFile',
};

const CheckForecastBulkDeletePanel = ({
    activeSubTab,
    onSubTabChange,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const tb = (path, params) => t(bulkDeleteKey(path), params);
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const role = resolveAdminRole(accessToken);
    const roleLabel = getRoleDisplayLabel(role);

    if (!canAccessCheckForecastBulkDelete(role)) {
        return null;
    }

    return (
        <div className="cfbd-panel">
            <section className="cfbd-section cfbd-section--header" aria-labelledby="cfbd-panel-title">
                <div className="cfbd-section__head">
                    <div>
                        <h3 id="cfbd-panel-title" className="cfbd-panel__title">
                            <FiTrash2 size={22} aria-hidden />
                            {tb('title')}
                        </h3>
                        <p className="cfbd-panel__desc">{tb('panelDescription')}</p>
                    </div>
                    <Badge
                        bg="primary"
                        className="cfbd-permission-badge"
                        aria-label={tb('permissionBadgeAria', { role: roleLabel })}
                    >
                        <FiShield size={14} className="me-1" aria-hidden />
                        {tb('permissionBadge', { role: roleLabel })}
                    </Badge>
                </div>
            </section>

            <Tab.Container
                activeKey={activeSubTab}
                onSelect={(key) => {
                    if (key && !disabled) onSubTabChange(key);
                }}
            >
                <Nav className="fb-segmented cfbd-mode-tabs" role="tablist" aria-label={tb('modes.ariaLabel')}>
                    <Nav.Item>
                        <Nav.Link eventKey={DELETE_MODE_KEYS.BY_DATE} disabled={disabled}>
                            <FiCalendar size={15} aria-hidden />
                            {tb('modes.byDate.tab')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey={DELETE_MODE_KEYS.BY_DATE_POLICY} disabled={disabled}>
                            <FiTrash2 size={15} aria-hidden />
                            {tb('modes.byDatePolicy.tab')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey={DELETE_MODE_KEYS.BY_FILE} disabled={disabled}>
                            <FiFileText size={15} aria-hidden />
                            {tb('modes.byFile.tab')}
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content className="cfbd-mode-content">
                    <Tab.Pane eventKey={DELETE_MODE_KEYS.BY_DATE}>
                        <CheckForecastBulkDeleteTab mode="byDate" showHeader={false} />
                    </Tab.Pane>
                    <Tab.Pane eventKey={DELETE_MODE_KEYS.BY_DATE_POLICY}>
                        <CheckForecastBulkDeleteTab mode="byDateAndPolicy" showHeader={false} />
                    </Tab.Pane>
                    <Tab.Pane eventKey={DELETE_MODE_KEYS.BY_FILE}>
                        <CheckForecastBulkDeleteFileTab />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
};

export default CheckForecastBulkDeletePanel;
